/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Send, Loader2, MessageSquareText, FileText, Pill, ArrowLeft, Trash2 } from 'lucide-react';
import { respondSecondOpinion, updateSecondOpinionStatus, cancelSecondOpinion } from '@/lib/marketplace';
import { cn } from '@/lib/utils';
import { StatusBadge, urgencyMeta } from './meta';

export const DetailPanel = ({ item, isDoctor, onRefresh, onBack }) => {
  const [response, setResponse] = useState('');
  const [recommendedAction, setRecommendedAction] = useState('');
  const [concurs, setConcurs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const submitResponse = async () => {
    if (response.trim().length < 10) { toast.error('Risposta troppo breve'); return; }
    setSaving(true);
    try {
      await respondSecondOpinion(item.id, {
        response: response.trim(),
        recommended_action: recommendedAction.trim() || undefined,
        concurs_with_original: concurs,
      });
      toast.success('Risposta inviata');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio risposta');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    setChangingStatus(true);
    try {
      await updateSecondOpinionStatus(item.id, status);
      toast.success('Stato aggiornato');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento stato');
    } finally {
      setChangingStatus(false);
    }
  };

  const [acting, setActing] = useState(false);
  const doCancel = async () => {
    if (acting || !window.confirm('Sei sicuro di voler annullare la richiesta?')) return;
    setActing(true);
    try {
      await cancelSecondOpinion(item.id);
      toast.success('Richiesta annullata');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore annullamento');
    } finally { setActing(false); }
  };

  return (
    <Card data-testid="so-detail">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {onBack && <Button size="icon" variant="ghost" onClick={onBack} className="lg:hidden"><ArrowLeft className="h-4 w-4" /></Button>}
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 flex-wrap">
                <MessageSquareText className="h-5 w-5 text-primary" /> Secondo parere
                <StatusBadge status={item.status} />
                <Badge variant="outline" className={cn(urgencyMeta[item.urgency]?.cls)}>Urgenza {urgencyMeta[item.urgency]?.label || item.urgency}</Badge>
              </CardTitle>
              <div className="text-xs text-muted-foreground mt-1">
                {isDoctor ? `Paziente: ${item.patient_name || 'Anonimo'}` : `Medico: ${item.doctor_name || 'N/D'} · ${item.doctor_specialty || ''}`}
                <span className="mx-1">·</span>
                {new Date(item.created_at).toLocaleString('it')}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Quesito clinico</div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{item.clinical_question}</p>
        </div>

        {(item.attached_prescriptions?.length > 0 || item.attached_notes?.length > 0) && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Allegati clinici</div>
              {item.attached_prescriptions?.length > 0 && (
                <div>
                  <div className="text-xs font-medium flex items-center gap-1"><Pill className="h-3.5 w-3.5" />Ricette ({item.attached_prescriptions.length})</div>
                  <ul className="text-xs text-muted-foreground pl-4 list-disc mt-1">
                    {item.attached_prescriptions.slice(0, 5).map((p) => (
                      <li key={p.id}>{p.code || p.id} · {p.diagnosis || 'senza diagnosi'} · {new Date(p.issued_at).toLocaleDateString('it')}</li>
                    ))}
                    {item.attached_prescriptions.length > 5 && <li>+{item.attached_prescriptions.length - 5} altre...</li>}
                  </ul>
                </div>
              )}
              {item.attached_notes?.length > 0 && (
                <div>
                  <div className="text-xs font-medium flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Note cliniche ({item.attached_notes.length})</div>
                </div>
              )}
            </div>
          </>
        )}

        {item.response && (
          <>
            <Separator />
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-primary font-bold flex items-center gap-1 mb-1">
                <MessageSquareText className="h-3 w-3" /> Risposta del medico
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="so-response-text">{item.response}</p>
              {item.recommended_action && (
                <div className="mt-2 text-xs"><span className="font-semibold">Azione consigliata:</span> {item.recommended_action}</div>
              )}
              {item.concurs_with_original != null && (
                <div className="mt-1 text-xs">
                  {item.concurs_with_original
                    ? <span className="text-emerald-600">Concorda con il medico precedente</span>
                    : <span className="text-amber-600">In disaccordo con il medico precedente</span>}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-2">Risposta inviata il {new Date(item.responded_at).toLocaleString('it')}</div>
            </div>
          </>
        )}

        {isDoctor && !['responded', 'declined', 'cancelled'].includes(item.status) && (
          <>
            <Separator />
            <div className="flex flex-wrap gap-2">
              {item.status === 'pending' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => changeStatus('accepted')} disabled={changingStatus} data-testid="so-accept-btn">Accetta</Button>
                  <Button size="sm" variant="outline" onClick={() => changeStatus('declined')} disabled={changingStatus} data-testid="so-decline-btn">Rifiuta</Button>
                </>
              )}
              {item.status === 'accepted' && (
                <Button size="sm" variant="outline" onClick={() => changeStatus('in_review')} disabled={changingStatus}>In esame</Button>
              )}
            </div>

            {['accepted', 'in_review'].includes(item.status) && (
              <div className="rounded-xl border border-border/60 p-3 space-y-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary">Componi la risposta</div>
                <Textarea rows={6} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="La tua valutazione clinica, in italiano..." data-testid="so-response-input" />
                <Input value={recommendedAction} onChange={(e) => setRecommendedAction(e.target.value)} placeholder="Azione consigliata (es. approfondimento cardiologico)" data-testid="so-recommended-action" />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Concordi con il collega?</span>
                  <Button size="sm" variant={concurs === true ? 'default' : 'outline'} onClick={() => setConcurs(true)}>Sì</Button>
                  <Button size="sm" variant={concurs === false ? 'default' : 'outline'} onClick={() => setConcurs(false)}>No</Button>
                </div>
                <Button onClick={submitResponse} disabled={saving} className="w-full" data-testid="so-submit-response">
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio...</> : <><Send className="h-4 w-4" /> Invia risposta</>}
                </Button>
              </div>
            )}
          </>
        )}

        {!isDoctor && !['responded', 'cancelled'].includes(item.status) && (
          <>
            <Separator />
            <Button size="sm" variant="outline" onClick={doCancel} disabled={acting} className="text-destructive" data-testid="so-cancel-btn"><Trash2 className="h-3.5 w-3.5" /> Annulla richiesta</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
